/**
 * Affiliate referral program — data-layer client (fs809).
 *
 * Thin typed wrapper around /api/v1/affiliate-referral for the operator's
 * own referral program view:
 *   POST   /codes                    mint or fetch the current user's code
 *   POST   /codes/rotate             rotate code
 *   GET    /codes/:affiliateId       look up an affiliate's code
 *   GET    /                         list tenant referrals (?referrer_id=)
 *   GET    /referrer/:id/summary     summary counts + totals
 *
 * Raw fetch is confined to this file per the dash D6 contract.
 */

// ---------------------------------------------------------------------------
// Types — mirror the API route + lib (routes/affiliate-referral.ts).
// ---------------------------------------------------------------------------

export type PayoutStatus = "pending" | "paid" | "forfeited";

export const PAYOUT_STATUSES: readonly PayoutStatus[] = [
	"pending",
	"paid",
	"forfeited",
] as const;

export interface ReferralCodeRow {
	affiliate_id: string;
	tenant_id: string;
	code: string;
	created_at: number;
	updated_at: number;
}

export interface ReferralRow {
	id: string;
	tenant_id: string;
	referrer_id: string;
	referee_id: string;
	referral_code: string;
	bonus_amount: number;
	bonus_currency: string;
	payout_status: PayoutStatus;
	paid_at: number | null;
	forfeited_at: number | null;
	forfeit_reason: string | null;
	created_at: number;
}

export interface ReferrerSummary {
	referrer_id: string;
	total_referrals: number;
	pending_count: number;
	paid_count: number;
	forfeited_count: number;
	pending_amount: number;
	paid_amount: number;
	currency: string | null;
}

// ---------------------------------------------------------------------------
// Program-settings view shape (derived locally from summary + code).
// The backend has no GET for "program settings"; the UI treats the stored
// bonus percentage as a static display unit driven by most-recent redemption.
// ---------------------------------------------------------------------------

export interface ProgramSettingsView {
	bonus_percent: number;
	currency: string;
}

export const DEFAULT_PROGRAM_SETTINGS: ProgramSettingsView = {
	bonus_percent: 10,
	currency: "USD",
};

// ---------------------------------------------------------------------------
// Fetch plumbing
// ---------------------------------------------------------------------------

const BASE = "/api/v1/affiliate-referral";

async function parseOrThrow<T>(res: Response): Promise<T> {
	const text = await res.text();
	let data: unknown = null;
	if (text) {
		try {
			data = JSON.parse(text);
		} catch {
			data = text;
		}
	}
	if (!res.ok) {
		const msg =
			data &&
			typeof data === "object" &&
			data !== null &&
			"error" in data &&
			typeof (data as { error: unknown }).error === "string"
				? (data as { error: string }).error
				: `HTTP ${res.status}`;
		throw new Error(msg);
	}
	return data as T;
}

// ---------------------------------------------------------------------------
// API calls
// ---------------------------------------------------------------------------

export async function mintCode(affiliateId: string): Promise<ReferralCodeRow> {
	const res = await fetch(`${BASE}/codes`, {
		method: "POST",
		credentials: "include",
		headers: {
			accept: "application/json",
			"content-type": "application/json",
		},
		body: JSON.stringify({ affiliate_id: affiliateId }),
	});
	return parseOrThrow<ReferralCodeRow>(res);
}

export async function rotateCode(
	affiliateId: string,
): Promise<ReferralCodeRow> {
	const res = await fetch(`${BASE}/codes/rotate`, {
		method: "POST",
		credentials: "include",
		headers: {
			accept: "application/json",
			"content-type": "application/json",
		},
		body: JSON.stringify({ affiliate_id: affiliateId }),
	});
	return parseOrThrow<ReferralCodeRow>(res);
}

export async function getCode(affiliateId: string): Promise<ReferralCodeRow | null> {
	const res = await fetch(
		`${BASE}/codes/${encodeURIComponent(affiliateId)}`,
		{
			method: "GET",
			credentials: "include",
			headers: { accept: "application/json" },
		},
	);
	if (res.status === 404) return null;
	return parseOrThrow<ReferralCodeRow>(res);
}

export async function listReferrals(
	params: { referrer_id?: string; status?: PayoutStatus } = {},
): Promise<ReferralRow[]> {
	const qs = new URLSearchParams();
	if (params.referrer_id) qs.set("referrer_id", params.referrer_id);
	if (params.status) qs.set("status", params.status);
	const url = qs.toString() ? `${BASE}?${qs.toString()}` : BASE;
	const res = await fetch(url, {
		method: "GET",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<ReferralRow[]>(res);
}

export async function getReferrerSummary(
	referrerId: string,
): Promise<ReferrerSummary> {
	const res = await fetch(
		`${BASE}/referrer/${encodeURIComponent(referrerId)}/summary`,
		{
			method: "GET",
			credentials: "include",
			headers: { accept: "application/json" },
		},
	);
	return parseOrThrow<ReferrerSummary>(res);
}

// ---------------------------------------------------------------------------
// Query keys (TanStack Query) — list=['affiliate-referral'], detail uses id.
// ---------------------------------------------------------------------------

export const affiliateReferralQueryKeys = {
	all: ["affiliate-referral"] as const,
	list: () => [...affiliateReferralQueryKeys.all] as const,
	detail: (id: string) => [...affiliateReferralQueryKeys.all, id] as const,
	code: (affiliateId: string) =>
		[...affiliateReferralQueryKeys.all, "code", affiliateId] as const,
	referrals: (referrerId: string) =>
		[...affiliateReferralQueryKeys.all, "referrals", referrerId] as const,
	summary: (referrerId: string) =>
		[...affiliateReferralQueryKeys.all, "summary", referrerId] as const,
};

// ---------------------------------------------------------------------------
// Pure helpers — formatting + derivation
// ---------------------------------------------------------------------------

const CURRENCY_MINOR_UNITS: Record<string, number> = {
	JPY: 0,
	KRW: 0,
	VND: 0,
	// everything else defaults to 2
};

function minorUnits(currency: string): number {
	return CURRENCY_MINOR_UNITS[currency.toUpperCase()] ?? 2;
}

export function formatMoney(amount: number, currency: string | null): string {
	const cur = (currency ?? "USD").toUpperCase();
	const units = minorUnits(cur);
	const divisor = Math.pow(10, units);
	const value = amount / divisor;
	try {
		return new Intl.NumberFormat("en-US", {
			style: "currency",
			currency: cur,
			minimumFractionDigits: units,
			maximumFractionDigits: units,
		}).format(value);
	} catch {
		return `${cur} ${value.toFixed(units)}`;
	}
}

export function formatInteger(n: number): string {
	return new Intl.NumberFormat("en-US").format(n);
}

export function formatDate(ms: number | null | undefined): string {
	if (!ms || !Number.isFinite(ms)) return "—";
	const d = new Date(ms);
	if (Number.isNaN(d.getTime())) return "—";
	return d.toLocaleDateString("en-US", {
		year: "numeric",
		month: "short",
		day: "2-digit",
	});
}

export function formatStatus(s: PayoutStatus): string {
	if (s === "pending") return "Pending";
	if (s === "paid") return "Paid";
	return "Forfeited";
}

/**
 * Build the full shareable link for a referral code. Uses the page's current
 * origin when available so staging/preview hosts are honoured without a
 * config round-trip.
 */
export function buildReferralLink(code: string, origin?: string): string {
	const base =
		origin ??
		(typeof globalThis !== "undefined" &&
		typeof (globalThis as { location?: { origin?: string } }).location ===
			"object"
			? ((globalThis as { location?: { origin?: string } }).location?.origin ??
				"")
			: "");
	const prefix = base.replace(/\/$/, "");
	return `${prefix}/r/${encodeURIComponent(code)}`;
}

/**
 * Total earned — sum of paid_amount across referrals; currency is the
 * summary's `currency`. Returns formatted string ready for the tile.
 */
export function totalEarnedText(summary: ReferrerSummary | null): string {
	if (!summary) return "—";
	return formatMoney(summary.paid_amount, summary.currency);
}

/**
 * Total pending — same idea, but for `pending_amount`.
 */
export function totalPendingText(summary: ReferrerSummary | null): string {
	if (!summary) return "—";
	return formatMoney(summary.pending_amount, summary.currency);
}

/**
 * Derive a per-referral "earnings" display — paid referrals show their bonus
 * as a credit; others show a zero in the referral's own currency.
 */
export function referralEarningsText(row: ReferralRow): string {
	if (row.payout_status === "paid") {
		return formatMoney(row.bonus_amount, row.bonus_currency);
	}
	return formatMoney(0, row.bonus_currency);
}
